import {
  Center,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { type KeyboardEvent, useContext, useState } from 'react';
import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
  updateProfile,
} from 'firebase/auth';
import { Link, useNavigate } from 'react-router-dom';
import {
  EMAIL_DELIVERY_TOAST_HINT,
  errorToastOptions,
  getAuthErrorMessage,
  isValidEmail,
  successToastOptions,
  validatePassword,
} from '@/helpers';
import { isDefinitelyOffensiveName } from '@/helpers/moderation/nameModeration';
import { buildAuthContinueUrl } from '@/helpers/auth/authUrls';
import { syncSignupNameToFirestore } from '@/helpers/auth/signupSync';
import type { FirebaseError } from 'firebase/app';
import { FirebaseContext } from '@/session/FirebaseContext';
import {
  AuthHeading,
  AuthPasswordField,
  AuthSwitchPrompt,
  PrimaryAuthButton,
  useAuthTheme,
} from './auth-ui';
import { AuthDivider, GoogleButton } from './utils';
import { BaseAuthCard } from './base-card';

export function SignUpCard(props: { triggerLogin: () => void }) {
  const { auth, db, funcs } = useContext(FirebaseContext);
  const toast = useToast();
  const navigate = useNavigate();
  const { mutedText, linkColor } = useAuthTheme();

  const [name, setName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
  const [isBusy, setIsBusy] = useState<boolean>(false);
  const [createAttempted, setCreateAttempted] = useState<boolean>(false);
  const [passwordError, setPasswordError] = useState<string>('');
  const [nameError, setNameError] = useState<string>('');

  const handleCreate = async () => {
    setCreateAttempted(true);
    setPasswordError('');

    if (name.trim().length === 0) {
      return;
    }
    if (isDefinitelyOffensiveName(name.trim())) {
      setNameError('That name is not allowed. Please choose another.');
      return;
    }
    setNameError('');
    if (!isValidEmail(email)) {
      return;
    }

    const pwCheck = validatePassword(password, confirmPassword);
    if (!pwCheck.valid) {
      setPasswordError(pwCheck.error ?? 'Invalid password.');
      return;
    }

    setIsBusy(true);
    try {
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      await updateProfile(cred.user, { displayName: name.trim() });
      await sendEmailVerification(cred.user, {
        url: buildAuthContinueUrl('/verifyEmail'),
        handleCodeInApp: true,
      });
      const trimmedName = name.trim();
      void syncSignupNameToFirestore(db, funcs, cred.user.uid, trimmedName).catch(console.error);
      toast(
        successToastOptions(
          'Account created',
          `We've sent a verification link from 10 Teams. Check your email to verify your address. ${EMAIL_DELIVERY_TOAST_HINT}`,
        ),
      );
      navigate('/verifyEmail', { replace: true, state: { emailJustSent: true } });
    } catch (err) {
      toast(errorToastOptions('Sign up failed', getAuthErrorMessage(err as FirebaseError)));
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <BaseAuthCard>
      <AuthHeading>Create an account</AuthHeading>
      <AuthSwitchPrompt actionLabel='Log in' onAction={props.triggerLogin}>
        Already have an account?
      </AuthSwitchPrompt>
      <VStack align='stretch' spacing={4}>
        <FormControl
          isInvalid={(createAttempted && name.trim().length === 0) || nameError.length > 0}
          isRequired
        >
          <FormLabel fontSize='sm' fontWeight='medium'>
            Name
          </FormLabel>
          <Input
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (nameError) setNameError('');
            }}
            type='text'
            autoComplete='name'
            size='md'
            borderRadius='md'
            onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
              if (e.key === 'Enter') handleCreate();
            }}
          />
          <FormErrorMessage>
            {nameError || 'A name is required.'}
          </FormErrorMessage>
        </FormControl>
        <FormControl isInvalid={createAttempted && !isValidEmail(email)} isRequired>
          <FormLabel fontSize='sm' fontWeight='medium'>
            Email
          </FormLabel>
          <Input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            type='email'
            autoComplete='email'
            size='md'
            borderRadius='md'
            onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
              if (e.key === 'Enter') handleCreate();
            }}
          />
          <FormErrorMessage>A valid email is required.</FormErrorMessage>
        </FormControl>
        <FormControl isInvalid={createAttempted && passwordError.length > 0} isRequired>
          <FormLabel fontSize='sm' fontWeight='medium'>
            Password
          </FormLabel>
          <AuthPasswordField
            value={password}
            onChange={setPassword}
            visible={showPassword}
            onToggleVisible={() => setShowPassword(!showPassword)}
            autoComplete='new-password'
            onEnter={handleCreate}
          />
          {passwordError ? <FormErrorMessage>{passwordError}</FormErrorMessage> : null}
        </FormControl>
        <FormControl isRequired>
          <FormLabel fontSize='sm' fontWeight='medium'>
            Confirm password
          </FormLabel>
          <AuthPasswordField
            value={confirmPassword}
            onChange={setConfirmPassword}
            visible={showConfirmPassword}
            onToggleVisible={() => setShowConfirmPassword(!showConfirmPassword)}
            autoComplete='new-password'
            onEnter={handleCreate}
          />
        </FormControl>
        <PrimaryAuthButton
          isDisabled={
            isBusy ||
            (createAttempted &&
              (name.trim().length === 0 || !isValidEmail(email) || passwordError.length > 0))
          }
          isLoading={isBusy}
          onClick={handleCreate}
        >
          Create account
        </PrimaryAuthButton>
      </VStack>
      <AuthDivider />
      <HStack pb={3}>
        <GoogleButton auth={auth}>Sign up with Google</GoogleButton>
      </HStack>
      <Center>
        <Text align='center' fontSize='xs' color={mutedText} lineHeight='tall'>
          By continuing, you agree to our{' '}
          <Link to='/Privacy_Policy.pdf?v=20260715' target='_blank' rel='noopener noreferrer'>
            <Text as='span' color={linkColor} fontWeight='medium'>
              Privacy Notice
            </Text>
          </Link>
        </Text>
      </Center>
    </BaseAuthCard>
  );
}
